import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, DollarSign, CreditCard, AlertCircle } from "lucide-react";
import { PaymentFrequency, InstallmentPlanFormData } from "@/types/stock";
import { format, addDays, addWeeks, addMonths } from "date-fns";

interface InstallmentPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  totalAmount: number;
  onConfirm: (plan: InstallmentPlanFormData) => void;
}

const InstallmentPlanDialog: React.FC<InstallmentPlanDialogProps> = ({
  open,
  onOpenChange,
  totalAmount,
  onConfirm,
}) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<InstallmentPlanFormData>({
    customerName: "",
    customerPhone: "",
    customerEmail: "",
    totalAmount: totalAmount,
    downPayment: 0,
    numberOfInstallments: 3,
    paymentFrequency: "monthly",
    startDate: new Date(),
    notes: "",
  });

  // Calculate installment details
  const installmentDetails = useMemo(() => {
    const remainingAmount = formData.totalAmount - formData.downPayment;
    const installmentAmount = remainingAmount / formData.numberOfInstallments;

    // Calculate payment schedule
    const schedule = [];
    let currentDate = formData.startDate;

    for (let i = 1; i <= formData.numberOfInstallments; i++) {
      if (formData.paymentFrequency === "weekly") {
        currentDate = addWeeks(formData.startDate, i);
      } else if (formData.paymentFrequency === "biweekly") {
        currentDate = addWeeks(formData.startDate, i * 2);
      } else {
        currentDate = addMonths(formData.startDate, i);
      }

      schedule.push({
        number: i,
        date: currentDate,
        amount: installmentAmount,
      });
    }

    return {
      remainingAmount,
      installmentAmount,
      schedule,
      endDate: currentDate,
    };
  }, [formData]);

  const handleSubmit = () => {
    if (!formData.customerName || !formData.customerPhone) {
      return;
    }

    onConfirm(formData);
    onOpenChange(false);
  };

  const isValid =
    formData.customerName &&
    formData.customerPhone &&
    formData.downPayment < formData.totalAmount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {t("stock.installmentPlan.title")}
          </DialogTitle>
          <DialogDescription>
            {t("stock.installmentPlan.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Customer Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm">
              {t("stock.installmentPlan.customerInfo")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customerName">
                  {t("stock.customerName")} *
                </Label>
                <Input
                  id="customerName"
                  value={formData.customerName}
                  onChange={(e) =>
                    setFormData({ ...formData, customerName: e.target.value })
                  }
                  placeholder={t("stock.enterCustomerName")}
                />
              </div>
              <div>
                <Label htmlFor="customerPhone">
                  {t("stock.customerPhone")} *
                </Label>
                <Input
                  id="customerPhone"
                  value={formData.customerPhone}
                  onChange={(e) =>
                    setFormData({ ...formData, customerPhone: e.target.value })
                  }
                  placeholder={t("stock.enterCustomerPhone")}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="customerEmail">
                  {t("stock.customerEmail")} ({t("common.optional")})
                </Label>
                <Input
                  id="customerEmail"
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, customerEmail: e.target.value })
                  }
                  placeholder={t("stock.enterCustomerEmail")}
                />
              </div>
            </div>
          </div>

          {/* Payment Plan Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm">
              {t("stock.installmentPlan.planDetails")}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="totalAmount">{t("stock.totalAmount")}</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="totalAmount"
                    type="number"
                    value={formData.totalAmount}
                    readOnly
                    className="pl-10 bg-gray-50"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="downPayment">
                  {t("stock.installmentPlan.downPayment")}
                </Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="downPayment"
                    type="number"
                    step="0.001"
                    value={formData.downPayment}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        downPayment: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="pl-10"
                    max={formData.totalAmount}
                  />
                </div>
              </div>

              <div>
                <Label>{t("stock.installmentPlan.remainingBalance")}</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={installmentDetails.remainingAmount.toFixed(3)}
                    readOnly
                    className="pl-10 bg-gray-50 font-semibold"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="numberOfInstallments">
                  {t("stock.installmentPlan.numberOfInstallments")}
                </Label>
                <Input
                  id="numberOfInstallments"
                  type="number"
                  min="2"
                  max="24"
                  value={formData.numberOfInstallments}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      numberOfInstallments: parseInt(e.target.value) || 2,
                    })
                  }
                />
              </div>

              <div>
                <Label htmlFor="paymentFrequency">
                  {t("stock.installmentPlan.paymentFrequency")}
                </Label>
                <Select
                  value={formData.paymentFrequency}
                  onValueChange={(value: PaymentFrequency) =>
                    setFormData({ ...formData, paymentFrequency: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">
                      {t("stock.installmentPlan.weekly")}
                    </SelectItem>
                    <SelectItem value="biweekly">
                      {t("stock.installmentPlan.biweekly")}
                    </SelectItem>
                    <SelectItem value="monthly">
                      {t("stock.installmentPlan.monthly")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="startDate">
                  {t("stock.installmentPlan.startDate")}
                </Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="startDate"
                    type="date"
                    value={format(formData.startDate, "yyyy-MM-dd")}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        startDate: new Date(e.target.value),
                      })
                    }
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">
                {t("stock.notes")} ({t("common.optional")})
              </Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder={t("stock.installmentPlan.notesPlaceholder")}
                rows={2}
              />
            </div>
          </div>

          {/* Payment Schedule Preview */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm">
              {t("stock.installmentPlan.paymentSchedule")}
            </h3>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="text-sm text-blue-600 dark:text-blue-400">
                  {t("stock.installmentPlan.installmentAmount")}
                </div>
                <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                  {installmentDetails.installmentAmount.toFixed(3)}{" "}
                  {t("common.currency")}
                </div>
              </div>

              <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="text-sm text-green-600 dark:text-green-400">
                  {t("stock.installmentPlan.totalInstallments")}
                </div>
                <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                  {formData.numberOfInstallments}
                </div>
              </div>

              <div className="p-4 bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-lg">
                <div className="text-sm text-purple-600 dark:text-purple-400">
                  {t("stock.installmentPlan.endDate")}
                </div>
                <div className="text-lg font-bold text-purple-700 dark:text-purple-300">
                  {format(installmentDetails.endDate, "MMM dd, yyyy")}
                </div>
              </div>
            </div>

            {/* Schedule Table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                        {t("stock.installmentPlan.paymentNumber")}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                        {t("stock.installmentPlan.dueDate")}
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">
                        {t("stock.installmentPlan.amount")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {installmentDetails.schedule.map((payment) => (
                      <tr key={payment.number}>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="outline">#{payment.number}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {format(payment.date, "MMM dd, yyyy")}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium">
                          {payment.amount.toFixed(3)} {t("common.currency")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Warning */}
          {formData.downPayment >= formData.totalAmount && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <div className="text-sm text-yellow-800 dark:text-yellow-200">
                {t("stock.installmentPlan.downPaymentWarning")}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isValid}
              className="flex-1"
            >
              {t("stock.installmentPlan.createPlan")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InstallmentPlanDialog;
